/**
 * Thin fetch wrapper used by web data hooks. Inserts the bearer token from
 * `useAuth().getAccessToken()` and unwraps the `{ data }` envelope.
 *
 * This module deliberately stays small — the typed Core client lives in
 * `packages/core-api-client`; web→apps/api calls are simple proxies.
 *
 * 5xx responses fire an opt-in "report this" side-effect via the registered
 * server-error reporter (W10-F5-S3). The reporter is a function set once at
 * app shell mount; `apiFetch` never imports the toast / Sentry libs
 * directly so unit tests stay lean.
 */
export const API_BASE_URL =
  ((import.meta as ImportMeta).env as Record<string, string | undefined>)
    .VITE_API_BASE_URL ?? 'http://localhost:3001';

export interface ApiFetchOpts extends RequestInit {
  getAccessToken?: () => Promise<string | null> | string | null;
  /** Skip the 5xx feedback toast — used by the support-tickets POST so the
   *  Report-this flow can't recursively trigger itself. */
  skipServerErrorReport?: boolean;
}

export interface ServerErrorContext {
  status: number;
  url: string;
  method: string;
  message: string;
}

/**
 * Typed `Error` thrown by `apiFetch` for non-2xx responses. Subclasses
 * the platform `Error` so existing `rejects.toThrow(message)` assertions
 * keep working, while adding the HTTP status + parsed envelope so
 * callers can branch on `code` / `tier` / `family` etc. without
 * re-parsing the message string.
 *
 * Introduced for W47-F5-S1 to let the upgrade-prompt modal read the
 * `ENTITLEMENT_DENIED` envelope. Generic enough to use for any 4xx
 * structured error going forward.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly envelope: Record<string, unknown> | undefined;

  constructor(
    message: string,
    status: number,
    envelope: Record<string, unknown> | undefined,
  ) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.envelope = envelope;
  }
}

/** Type-guard for callers reading the structured envelope. */
export function isApiError(value: unknown): value is ApiError {
  return value instanceof ApiError;
}

let serverErrorReporter: ((ctx: ServerErrorContext) => void) | undefined;

/**
 * Register a reporter to be called on 5xx. AppShell wires this up to the
 * "Something went wrong — report this?" toast / FeedbackModal pre-fill.
 */
export function setServerErrorReporter(
  fn: (ctx: ServerErrorContext) => void,
): () => void {
  serverErrorReporter = fn;
  return () => {
    if (serverErrorReporter === fn) serverErrorReporter = undefined;
  };
}

/**
 * W102-F1-S3 — structured 4xx error context emitted to a global reporter.
 *
 * Mirrors `ServerErrorContext` (5xx) but surfaces the parsed envelope so
 * the consumer can branch on `envelope.code`. Used by
 * `<ActiveOrgErrorBridge />` to toast resolveActiveOrg 403 codes
 * (ORG_NOT_IN_MEMBERSHIPS / NO_HOME_ORG / NO_ACTIVE_ORG) and
 * AuthGuard 503 codes (CORE_USERS_ME_UNAVAILABLE).
 */
export interface ClientErrorContext {
  status: number;
  url: string;
  method: string;
  envelope: Record<string, unknown> | undefined;
}

let clientErrorReporter: ((ctx: ClientErrorContext) => void) | undefined;

/**
 * W102-F1-S3 — register a global reporter that receives 4xx (+ select 5xx)
 * responses with a structured envelope. AppShell wires this up to the
 * `<ActiveOrgErrorBridge />` so resolveActiveOrg 403s surface as a
 * helpful "switch to org X" toast rather than a silent fail.
 *
 * Mirrors `setServerErrorReporter` (5xx → "report this?" toast) but the
 * two paths are independent — they fire for different status ranges
 * with different envelope shapes.
 */
export function setClientErrorReporter(
  fn: (ctx: ClientErrorContext) => void,
): () => void {
  clientErrorReporter = fn;
  return () => {
    if (clientErrorReporter === fn) clientErrorReporter = undefined;
  };
}

let activeOrgIdGetter: (() => string | null | undefined) | undefined;

/**
 * W102-F1-S2 — register a getter that returns the SPA's current active org id.
 *
 * Every outbound `apiFetch` call reads this on demand and stamps
 * `X-Org-Id: <value>` onto the request when the getter returns a non-empty
 * string. The backend `resolveActiveOrg` helper (W100-F2-S2) validates the
 * header against the user's allowed-org set (homeOrgId ∪ memberships
 * organization_ids) and 403s on mismatch.
 *
 * Wired once from `AppShell` via the `useActiveOrgIdSync()` hook. Returns
 * an unregister cleanup so test harnesses can reset between cases.
 */
export function setActiveOrgIdGetter(
  fn: () => string | null | undefined,
): () => void {
  activeOrgIdGetter = fn;
  return () => {
    if (activeOrgIdGetter === fn) activeOrgIdGetter = undefined;
  };
}

export async function apiFetch<T>(
  path: string,
  opts: ApiFetchOpts = {},
): Promise<T> {
  const headers = new Headers(opts.headers);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (opts.getAccessToken) {
    const token = await Promise.resolve(opts.getAccessToken());
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  // W102-F1-S2 — stamp X-Org-Id from the registered active-org getter
  // when the caller hasn't supplied one explicitly. The backend
  // resolveActiveOrg helper (W100-F2-S2) reads this header to pick the
  // request's active org from the user's memberships.
  if (!headers.has('X-Org-Id') && activeOrgIdGetter) {
    const orgId = activeOrgIdGetter();
    if (orgId && typeof orgId === 'string' && orgId.length > 0) {
      headers.set('X-Org-Id', orgId);
    }
  }
  const method = (opts.method ?? 'GET').toUpperCase();
  const res = await fetch(`${API_BASE_URL}${path}`, { ...opts, headers });
  const text = await res.text();
  const parsed = text ? (JSON.parse(text) as unknown) : undefined;
  if (!res.ok) {
    const env = parsed as { error?: { message?: string } } | undefined;
    const message = env?.error?.message ?? `HTTP ${res.status}`;
    if (
      res.status >= 500 &&
      !opts.skipServerErrorReport &&
      serverErrorReporter
    ) {
      try {
        serverErrorReporter({
          status: res.status,
          url: path,
          method,
          message,
        });
      } catch {
        // reporter must never throw into the fetch path
      }
    }
    // Extract a structured envelope so callers can branch on `code` /
    // `tier` / `family` (e.g. W47-F5 ENTITLEMENT_DENIED). The Nest
    // exception filter wraps the body under `error` for 4xx; older
    // routes return the body directly — prefer `error`, fall back to
    // the raw payload.
    let envelope: Record<string, unknown> | undefined;
    if (parsed && typeof parsed === 'object') {
      const rec = parsed as Record<string, unknown>;
      if (rec.error && typeof rec.error === 'object') {
        envelope = rec.error as Record<string, unknown>;
      } else {
        envelope = rec;
      }
    }
    // W102-F1-S3 — fire the client-error reporter for structured 4xx
    // envelopes (and the W100-F2-S1 503 CORE_USERS_ME_UNAVAILABLE).
    // Lets `<ActiveOrgErrorBridge />` surface resolveActiveOrg 403s
    // with a "switch org" CTA instead of leaving every caller to
    // handle each code individually.
    if (
      clientErrorReporter &&
      envelope &&
      typeof envelope.code === 'string'
    ) {
      try {
        clientErrorReporter({ status: res.status, url: path, method, envelope });
      } catch {
        // reporter must never throw into the fetch path
      }
    }
    throw new ApiError(message, res.status, envelope);
  }
  // The TransformInterceptor on apps/api wraps every response in `{ data }`.
  const wrapped = parsed as { data?: T };
  return (wrapped?.data ?? parsed) as T;
}
